import React from "react";
import { Modal, Tag, Card, Badge, Tooltip } from "antd";
import {
    UserOutlined,
    ShoppingCartOutlined,
    PhoneOutlined,
    MailOutlined,
    TagOutlined,
    HomeOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    GiftOutlined,
    StarOutlined,
} from "@ant-design/icons";
import moment from "moment";

// @@ AI-COMMENT: Props interface for the RFQ Care session details modal
interface RfqCareSessionDetailsProps {
    open: boolean;
    session: any;
    onClose: () => void;
}

// @@ AI-COMMENT: Map of current state values to badge colors
const STATE_COLORS: Record<string, string> = {
    INITIAL: "default",
    SUGGEST_ALTERNATIVE: "blue",
    SHOW_MORE_ALTERNATIVES: "geekblue",
    SHOW_BUY_OPTIONS: "purple",
    STOCK_ALERT: "orange",
    WAITING_FOR_STOCK: "gold",
    ASK_QUANTITY: "cyan",
    ASK_BULK_BUDGET: "volcano",
    ASK_ADDRESS: "lime",
    CONFIRM_ADDRESS: "green",
};

// @@ AI-COMMENT: CDN base URL for product images
const CDN_BASE = "https://cdn.moglix.com/";

// ── WhatsApp wallpaper pattern ────────────────────────────────────────────────
const WA_BG = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8b8a0' fill-opacity='0.18'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

// ── SVG icons ─────────────────────────────────────────────────────────────────
const DoubleTickIcon = () => (
    <svg viewBox="0 0 16 11" fill="currentColor" style={{ width: 14, height: 10, color: '#53BDEB', display: 'inline-block', marginLeft: 2 }}>
        <path d="M11.071.653a.375.375 0 0 0-.531.02L5.85 5.765 3.84 3.493a.375.375 0 1 0-.567.492l2.302 2.605a.375.375 0 0 0 .549.014L11.09 1.18a.375.375 0 0 0-.019-.528zM15.071.653a.375.375 0 0 0-.531.02L9.85 5.765l-.47-.531a.375.375 0 1 0-.566.492l.75.849a.375.375 0 0 0 .548.014L15.09 1.18a.375.375 0 0 0-.019-.528zM.379 6.1a.375.375 0 0 0 0 .75h1.5a.375.375 0 0 0 0-.75H.379z" />
    </svg>
);

// @@ AI-COMMENT: Renders a single bot message with intent and state transition metadata
const BotMessage: React.FC<{ msg: any }> = ({ msg }) => {
    const { message, metadata, timestamp } = msg;
    const time = moment(timestamp).format("HH:mm");
    const hasButtons = metadata?.buttons?.length > 0;

    // @@ AI-COMMENT: Format markdown-style bold text (*text*) into JSX bold spans
    const formatMessage = (text: string) => {
        const parts = text.split(/(\*[^*]+\*)/g);
        return parts.map((part, i) =>
            part.startsWith("*") && part.endsWith("*") ? (
                <strong key={i}>{part.slice(1, -1)}</strong>
            ) : (
                <span key={i}>{part}</span>
            )
        );
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}>
            <div style={{ position: 'relative', maxWidth: '82%' }}>
                {/* Bubble tail */}
                <span style={{
                    position: 'absolute', top: 0, left: -6,
                    width: 0, height: 0, borderStyle: 'solid',
                    borderWidth: '0 8px 8px 0',
                    borderColor: 'transparent #FFFFFF transparent transparent',
                }} />
                <div style={{
                    background: '#FFFFFF',
                    borderRadius: '2px 12px 12px 12px',
                    padding: '7px 10px 5px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.13)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: '#1a1a1a',
                }}>
                    {/* Intent badge */}
                    {metadata?.intent && metadata.intent !== 'UNKNOWN' && (
                        <div style={{ marginBottom: 4 }}>
                            <span style={{
                                fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                background: '#e8f4fd', color: '#1a73e8',
                                padding: '1px 5px', borderRadius: 4,
                                textTransform: 'uppercase',
                            }}>{metadata.intent}</span>
                        </div>
                    )}
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {formatMessage(message)}
                    </p>
                    {hasButtons && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, paddingTop: 7, borderTop: '1px solid #f0f0f0' }}>
                            {metadata.buttons.map((btn: any) => (
                                <span key={btn.id} style={{
                                    fontSize: 11, fontWeight: 600,
                                    padding: '3px 9px', borderRadius: 12,
                                    border: '1px solid rgba(18,140,126,0.35)',
                                    color: '#128C7E', background: 'rgba(18,140,126,0.06)',
                                    cursor: 'default',
                                }}>{btn.title}</span>
                            ))}
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                        <span style={{ fontSize: 10, color: '#999' }}>{time}</span>
                    </div>
                </div>
                {/* State transition label */}
                {metadata?.state_transition && (
                    <div style={{ marginTop: 2, marginLeft: 2, fontSize: 9, color: '#bbb', fontFamily: 'monospace' }}>
                        {metadata.state_transition} · conf: {metadata.confidence}
                    </div>
                )}
            </div>
        </div>
    );
};

// @@ AI-COMMENT: Renders a single user message bubble
const UserMessage: React.FC<{ msg: any }> = ({ msg }) => {
    const { message, timestamp } = msg;
    const time = moment(timestamp).format("HH:mm");
    const isButtonAction = message.startsWith("btn_") || message.startsWith("selected_");

    return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <div style={{ position: 'relative', maxWidth: '82%' }}>
                {/* Bubble tail */}
                <span style={{
                    position: 'absolute', top: 0, right: -6,
                    width: 0, height: 0, borderStyle: 'solid',
                    borderWidth: '0 0 8px 8px',
                    borderColor: 'transparent transparent transparent #DCF8C6',
                }} />
                <div style={{
                    background: '#DCF8C6',
                    borderRadius: '12px 2px 12px 12px',
                    padding: '7px 10px 5px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.13)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: '#1a1a1a',
                }}>
                    {isButtonAction && (
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#128C7E', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>
                            Button Reply
                        </div>
                    )}
                    <p style={{ margin: 0, wordBreak: 'break-word', fontFamily: isButtonAction ? 'monospace' : 'inherit', fontSize: isButtonAction ? 11 : 13 }}>
                        {message}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2, gap: 1 }}>
                        <span style={{ fontSize: 10, color: '#667' }}>{time}</span>
                        <DoubleTickIcon />
                    </div>
                </div>
            </div>
        </div>
    );
};

// @@ AI-COMMENT: Renders a small product card for similar products list
const SimilarProductCard: React.FC<{ product: any }> = ({ product }) => (
    <div className="flex gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors">
        {product.main_image_link && (
            // @@ AI-COMMENT: Prepend CDN base URL to the relative image path
            <img
                src={`${CDN_BASE}${product.main_image_link}`}
                alt={product.product_name}
                className="w-12 h-12 object-contain flex-shrink-0 rounded bg-white border border-gray-100"
                onError={(e: any) => { e.target.style.display = 'none'; }}
            />
        )}
        <div className="flex-1 min-w-0">
            <div className="text-[10px] font-medium text-gray-800 leading-tight line-clamp-2 mb-0.5">
                {product.product_name}
            </div>
            <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-blue-600">₹{product.sales_price?.toLocaleString()}</span>
                {product.rating > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px] text-amber-500">
                        <StarOutlined className="text-[9px]" />
                        {product.rating}
                    </span>
                )}
            </div>
            <div className="text-[9px] text-gray-400">{product.brand_name}</div>
        </div>
    </div>
);

// @@ AI-COMMENT: Main session details modal component
const RfqCareSessionDetails: React.FC<RfqCareSessionDetailsProps> = ({ open, session, onClose }) => {

    if (!session) return null;

    const { chat_history, context, current_state, user_id, created_at, last_activity } = session;
    const stateColor = STATE_COLORS[current_state] || "default";
    const userInfo = context?.user_info || {};
    const rfqProductInfo = context?.rfq_product_info || {};
    const bestMatch = context?.best_matching_product;
    const rfqProductStatus = context?.rfq_product_status || {};
    const isInStock = rfqProductStatus?.in_stock;
    const orderInfo = context?.order_info;
    const selectedAddress = context?.selected_address;
    const addressOptions = context?.address_options || [];
    const similarProducts = context?.similar_products || [];
    const selectedProductInfo = context?.selected_product_info;

    // @@ AI-COMMENT: Count user vs bot messages for the summary stats
    const userMsgCount = chat_history?.filter((m: any) => m.sender === "user").length || 0;
    const botMsgCount = chat_history?.filter((m: any) => m.sender === "bot").length || 0;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={960}
            footer={null}
            title={
                <div className="flex items-center gap-3">
                    <ShoppingCartOutlined className="text-blue-600 text-xl" />
                    <span className="text-lg font-semibold">RFQ Care Session</span>
                    <Tag color="blue" className="font-mono text-xs">{user_id}</Tag>
                    <Tag color={stateColor}>{current_state}</Tag>
                </div>
            }
            styles={{ body: { padding: "24px", backgroundColor: "#f9fafb", minHeight: "600px" } }}
            centered
        >
            <div className="space-y-5">
                {/* @@ AI-COMMENT: Session summary stat cards at the top */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card size="small" className="shadow-sm">
                        <div className="text-gray-400 text-xs mb-1">User</div>
                        <div className="font-semibold text-gray-800 text-sm truncate">{userInfo.first_name || "—"}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                            <PhoneOutlined className="text-[10px]" /> {userInfo.mobile || "—"}
                        </div>
                    </Card>
                    <Card size="small" className="shadow-sm">
                        <div className="text-gray-400 text-xs mb-1">Product Enquired</div>
                        <Tooltip title={rfqProductInfo.product_name}>
                            <div className="font-medium text-gray-800 text-xs leading-snug line-clamp-2">
                                {rfqProductInfo.product_name || "—"}
                            </div>
                        </Tooltip>
                    </Card>
                    <Card size="small" className="shadow-sm">
                        <div className="text-gray-400 text-xs mb-1">Stock Status</div>
                        {isInStock === true ? (
                            <Badge status="success" text={<span className="text-xs text-green-600 font-medium">In Stock ✅</span>} />
                        ) : rfqProductStatus && Object.keys(rfqProductStatus).length === 0 ? (
                            <Badge status="error" text={<span className="text-xs text-red-500 font-medium">Out of Stock</span>} />
                        ) : (
                            <span className="text-xs text-gray-400">—</span>
                        )}
                        <div className="text-[10px] text-gray-400 mt-1">{userMsgCount} user · {botMsgCount} bot msgs</div>
                    </Card>
                    <Card size="small" className="shadow-sm">
                        <div className="text-gray-400 text-xs mb-1">Timeline</div>
                        <div className="text-xs text-gray-700">
                            <span className="font-medium">Start:</span> {moment(created_at).format("MMM DD, HH:mm")}
                        </div>
                        <div className="text-xs text-gray-500">
                            <span className="font-medium">Last:</span> {moment(last_activity).format("MMM DD, HH:mm")}
                        </div>
                    </Card>
                </div>

                {/* @@ AI-COMMENT: Order info banner shown only when an order exists */}
                {orderInfo && (
                    <div className={`flex items-center gap-4 px-4 py-3 rounded-xl border ${orderInfo.order_id ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                        {orderInfo.order_id ? (
                            <CheckCircleOutlined className="text-green-500 text-lg flex-shrink-0" />
                        ) : (
                            <CloseCircleOutlined className="text-red-400 text-lg flex-shrink-0" />
                        )}
                        <div className="flex flex-wrap gap-x-6 gap-y-1 flex-1">
                            {orderInfo.order_id && (
                                <div>
                                    <span className="text-[10px] text-gray-500 block">Order ID</span>
                                    <span className="text-sm font-bold text-green-700 font-mono">#{orderInfo.order_id}</span>
                                </div>
                            )}
                            {orderInfo.order_amount && (
                                <div>
                                    <span className="text-[10px] text-gray-500 block">Amount</span>
                                    <span className="text-sm font-bold text-gray-800">₹{orderInfo.order_amount?.toLocaleString()}</span>
                                </div>
                            )}
                            {orderInfo.mode && (
                                <div>
                                    <span className="text-[10px] text-gray-500 block">Payment Mode</span>
                                    <Tag color="purple" className="text-xs">{orderInfo.mode}</Tag>
                                </div>
                            )}
                        </div>
                        <span className="text-xs font-medium text-green-600">Order Placed</span>
                    </div>
                )}

                {/* @@ AI-COMMENT: Two-column layout: chat on the left, context panel on the right */}
                <div className="flex gap-4">
                    {/* ── WhatsApp-style Chat History ── */}
                    <div style={{ width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}>
                        {/* Header bar */}
                        <div style={{ background: '#075E54', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#128C7E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg viewBox="0 0 24 24" fill="white" style={{ width: 18, height: 18 }}>
                                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                                </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, color: '#fff', fontWeight: 600, fontSize: 14, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    +91 {userInfo.mobile || user_id}
                                </p>
                                <p style={{ margin: 0, color: '#aed6a6', fontSize: 11 }}>
                                    {chat_history?.length || 0} messages
                                </p>
                            </div>
                            <Tag color={stateColor} style={{ flexShrink: 0, fontSize: 10 }}>{current_state}</Tag>
                        </div>

                        {/* Chat wallpaper */}
                        <div style={{
                            flex: 1, overflowY: 'auto', padding: '12px 12px 4px',
                            background: '#ECE5DD', backgroundImage: WA_BG,
                            maxHeight: 520,
                        }}>
                            {/* Date chip */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                                <span style={{ background: 'rgba(255,255,255,0.7)', color: '#666', fontSize: 10, padding: '2px 10px', borderRadius: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                                    {moment(created_at).format('D MMM YYYY')}
                                </span>
                            </div>

                            {chat_history && chat_history.length > 0 ? (
                                chat_history.map((msg: any, idx: number) => (
                                    <div key={idx}>
                                        {msg.sender === "user" ? (
                                            <UserMessage msg={msg} />
                                        ) : (
                                            <BotMessage msg={msg} />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: '#999', fontSize: 13 }}>No messages found</div>
                            )}
                        </div>

                        {/* Footer state bar */}
                        <div style={{ background: '#F0F0F0', borderTop: '1px solid #ddd', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <span style={{ fontSize: 11, color: '#888' }}>Current state:</span>
                            <Tag color={stateColor} style={{ fontSize: 11, margin: 0 }}>{current_state}</Tag>
                        </div>
                    </div>

                    {/* Context Panel */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', maxHeight: 600 }}>

                        {/* Best Matching Product */}
                        {bestMatch && bestMatch.product_name && (
                            <Card
                                size="small"
                                title={<span className="text-xs font-semibold text-gray-600">Best Match Suggested</span>}
                                className="shadow-sm"
                            >
                                {bestMatch.main_image_link && (
                                    // @@ AI-COMMENT: Prepend CDN base URL to the relative image path
                                    <img
                                        src={`${CDN_BASE}${bestMatch.main_image_link}`}
                                        alt={bestMatch.product_name}
                                        className="w-full h-24 object-contain mb-2 rounded bg-gray-50 border border-gray-100"
                                        onError={(e: any) => { e.target.style.display = 'none'; }}
                                    />
                                )}
                                <div className="text-xs font-medium text-gray-800 leading-snug mb-1">
                                    {bestMatch.product_name}
                                </div>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-bold text-blue-600">₹{bestMatch.sales_price?.toLocaleString()}</span>
                                    {bestMatch.rating > 0 && (
                                        <Tag color="gold" className="text-xs">{bestMatch.rating} ⭐</Tag>
                                    )}
                                </div>
                                <div className="text-[10px] text-gray-400">{bestMatch.brand_name}</div>
                            </Card>
                        )}

                        {/* Selected Product Info */}
                        {selectedProductInfo?.name && (
                            <Card
                                size="small"
                                title={<span className="text-xs font-semibold text-gray-600">Selected Product</span>}
                                className="shadow-sm border-blue-100"
                            >
                                <div className="space-y-1.5">
                                    <div className="text-xs font-medium text-gray-800 leading-snug">{selectedProductInfo.name}</div>
                                    {selectedProductInfo.price && (
                                        <div className="flex items-center gap-2">
                                            <TagOutlined className="text-gray-400 text-xs" />
                                            <span className="text-xs font-bold text-blue-600">₹{selectedProductInfo.price?.toLocaleString()}</span>
                                        </div>
                                    )}
                                    {selectedProductInfo.returnable !== undefined && (
                                        // @@ AI-COMMENT: Show returnable badge based on product policy
                                        <Tag color={selectedProductInfo.returnable ? "green" : "red"} className="text-[10px]">
                                            {selectedProductInfo.returnable ? "Returnable" : "Non-Returnable"}
                                        </Tag>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* User Info Details */}
                        <Card
                            size="small"
                            title={<span className="text-xs font-semibold text-gray-600">User Details</span>}
                            className="shadow-sm"
                        >
                            <div className="space-y-1.5">
                                <div className="flex items-start gap-2">
                                    <UserOutlined className="text-gray-400 mt-0.5 text-xs" />
                                    <div>
                                        <div className="text-xs font-medium">{userInfo.first_name || "—"}</div>
                                        <div className="text-[10px] text-gray-400">ID: {userInfo.user_id}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <PhoneOutlined className="text-gray-400 text-xs" />
                                    <span className="text-xs font-mono">{userInfo.mobile}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <MailOutlined className="text-gray-400 text-xs" />
                                    <span className="text-xs text-gray-600 break-all">{userInfo.email || "—"}</span>
                                </div>
                            </div>
                        </Card>

                        {/* Buy Context */}
                        {(context?.buy_type || context?.quantity || selectedProductInfo) && (
                            <Card
                                size="small"
                                title={<span className="text-xs font-semibold text-gray-600">Purchase Intent</span>}
                                className="shadow-sm"
                            >
                                <div className="space-y-1.5">
                                    {context.buy_type && (
                                        <div className="flex items-center gap-2">
                                            <TagOutlined className="text-gray-400 text-xs" />
                                            <span className="text-xs">Buy Type: <Tag color="purple" className="text-xs">{context.buy_type}</Tag></span>
                                        </div>
                                    )}
                                    {context.quantity && (
                                        <div className="text-xs text-gray-600">Quantity: <strong>{context.quantity}</strong></div>
                                    )}
                                    {context.is_order_attempted && (
                                        <Tag color="orange" className="text-[10px]">Order Attempted</Tag>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* Selected & Available Addresses */}
                        {(selectedAddress?.full_address || addressOptions.length > 0) && (
                            <Card
                                size="small"
                                title={
                                    <span className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                        <HomeOutlined />
                                        Delivery Address
                                    </span>
                                }
                                className="shadow-sm"
                            >
                                {selectedAddress?.full_address && (
                                    <div className="mb-2">
                                        <div className="flex items-center gap-1 mb-1">
                                            <CheckCircleOutlined className="text-green-500 text-[10px]" />
                                            <span className="text-[10px] text-green-600 font-medium">Selected</span>
                                        </div>
                                        <div className="text-xs text-gray-700 whitespace-pre-line leading-snug bg-green-50 rounded p-1.5 border border-green-100">
                                            {selectedAddress.full_address}
                                        </div>
                                    </div>
                                )}
                                {/* @@ AI-COMMENT: List all available address options for the user */}
                                {addressOptions.length > 1 && (
                                    <div>
                                        <div className="text-[10px] text-gray-400 mb-1">All Options ({addressOptions.length})</div>
                                        <div className="space-y-1">
                                            {addressOptions.map((addr: any) => (
                                                <div key={addr.id} className="text-[10px] text-gray-500 bg-gray-50 rounded p-1 border border-gray-100 leading-snug">
                                                    <span className="font-medium text-gray-600">{addr.name}</span>
                                                    <br />
                                                    {addr.full_address}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )}

                        {/* Similar Products */}
                        {similarProducts.length > 0 && (
                            <Card
                                size="small"
                                title={
                                    <span className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                        <GiftOutlined />
                                        Similar Products ({similarProducts.length})
                                    </span>
                                }
                                className="shadow-sm"
                                styles={{ body: { padding: "8px", maxHeight: "280px", overflowY: "auto" } }}
                            >
                                {/* @@ AI-COMMENT: Render all similar products with CDN images */}
                                <div className="space-y-1.5">
                                    {similarProducts.map((product: any) => (
                                        <SimilarProductCard key={product.product_id} product={product} />
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default RfqCareSessionDetails;
